// src/components/navigation/rail.tsx
import React from "react";
import { Link, useLocation } from "react-router";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button-new";
import { PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { Fab } from "./fab";
import { useNavigationRail } from "@/components/navigation/rail-context";
import { UserMenu } from "./user-menu";
import { AppLogo } from "./app-logo";

export interface NavItem {
    title: string;
    url: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    requiresAdmin?: boolean;
}

export interface NavGroup {
    title: string;
    items: NavItem[];
}

interface NavigationRailProps {
    groups: NavGroup[];
    isAdmin: boolean;
    signOut?: () => void;
    userName?: string;
    userAvatarUrl?: string;
    autoExpand?: boolean;
}

export function NavigationRail({
    groups,
    isAdmin,
    signOut,
    userName = "User",
    userAvatarUrl,
}: NavigationRailProps) {
    const { expanded, setExpanded, isMobile, isDrawerOpen, setDrawerOpen } =
        useNavigationRail();
    const location = useLocation();

    const hideExpandButton = false;

    // Close drawer when clicking a link on mobile
    const handleNavClick = () => {
        if (isMobile) {
            setDrawerOpen(false);
        }
    };

    return (
        <>
            {/* Mobile App Bar - Commented out in favor of the new AppBar component */}
            {/* 
            <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-md-surface border-b border-md-outline-variant z-20 flex items-center px-4">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDrawerOpen(true)}
                    className="mr-4"
                >
                    <Menu size={24} />
                </Button>
                <div className="flex-1 text-lg font-medium">Receiptrex</div>
            </div>
            */}

            {/* Mobile drawer overlay */}
            {isMobile && isDrawerOpen && (
                <div
                    className="md:hidden fixed inset-0 bg-black/50 z-30"
                    onClick={() => setDrawerOpen(false)}
                />
            )}

            {/* Navigation Rail/Drawer - adapts to mobile/desktop */}
            <div
                className={cn(
                    "fixed left-0 top-0 bottom-0  -bg-md-surface-variant  bg-md-surface-level-1 -shadow-sm -border-r border-md-outline-variant transition-all duration-300",
                    // Desktop styles
                    "hidden md:flex md:flex-col z-10",
                    expanded ? "md:w-64" : "md:w-20",
                    // Mobile styles
                    isMobile && "flex flex-col md:hidden w-[280px] z-40",
                    isMobile &&
                        (isDrawerOpen ? "translate-x-0" : "-translate-x-full"),
                )}
            >
                {/* App logo/brand - same height as app bar */}
                <div className="flex justify-between items-center h-20 -border-b border-md-outline-variant px-2">
                    <AppLogo collapsed={!expanded && !isMobile} />
                    {/* Close button - only on mobile */}
                    {isMobile && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDrawerOpen(false)}
                        >
                            <X size={20} />
                        </Button>
                    )}
                </div>

                {/* Expand/collapse button - styled like navigation items */}
                {!hideExpandButton && !isMobile && (
                    <div className="px-4 py-4">
                        <div
                            onClick={() => setExpanded(!expanded)}
                            className={cn(
                                "flex items-center px-3 rounded-md transition-all duration-300 cursor-pointer",
                                "hover:bg-md-surface-variant",
                                expanded
                                    ? "w-full gap-1 py-3"
                                    : "w-auto gap-0 py-2",
                            )}
                        >
                            {expanded ? (
                                <PanelLeftClose
                                    className="h-6 w-6"
                                    strokeWidth={1.5}
                                />
                            ) : (
                                <PanelLeftOpen
                                    className="h-6 w-6"
                                    strokeWidth={1.5}
                                />
                            )}
                        </div>
                    </div>
                )}

                {/* Navigation groups */}
                <div className="flex-1 overflow-y-auto py-2 flex flex-col gap-2">
                    {/* Desktop FAB - only shown in rail */}
                    {!isMobile && (
                        <div className={cn(expanded ? "pb-4" : "pb-0")}>
                            <Fab />
                        </div>
                    )}
                    {groups.map((group, groupIndex) => {
                        // Filter items based on admin status
                        const visibleItems = group.items.filter(
                            (item) => !item.requiresAdmin || isAdmin,
                        );

                        // Skip empty groups
                        if (visibleItems.length === 0) return null;

                        return (
                            <div
                                key={group.title}
                                className={cn(
                                    "transition-all duration-300",
                                    expanded || isMobile ? "px-4" : "px-2",
                                )}
                            >
                                {/* Group label with transition */}
                                <div
                                    className="px-3 mb-2 overflow-hidden transition-all duration-300"
                                    style={{
                                        maxHeight:
                                            expanded || isMobile
                                                ? "24px"
                                                : "0px",
                                        opacity: expanded || isMobile ? 1 : 0,
                                    }}
                                >
                                    <span className="text-xs font-medium text-md-on-surface-variant uppercase tracking-wider">
                                        {group.title}
                                    </span>
                                </div>

                                {/* Group items - Material Design v3 style with icons above labels */}
                                <div
                                    className={cn(
                                        "flex flex-col items-start -p-2 transition-all duration-50",
                                        expanded || isMobile
                                            ? "gap-2"
                                            : "gap-4",
                                    )}
                                >
                                    {visibleItems.map((item) => {
                                        const isActive =
                                            location.pathname === item.url;
                                        const Icon = item.icon;

                                        return (
                                            <Link
                                                key={item.title}
                                                to={item.url}
                                                onClick={handleNavClick}
                                                className={cn(
                                                    "flex flex-col items-center",
                                                )}
                                            >
                                                <div
                                                    className={cn(
                                                        "flex items-center px-3 rounded-lg transition-all duration-300",
                                                        isActive
                                                            ? "bg-md-primary-container"
                                                            : "hover:bg-md-surface-variant",
                                                        expanded || isMobile
                                                            ? "w-full gap-1 py-3"
                                                            : "w-auto gap-0 py-1.5",
                                                    )}
                                                >
                                                    <Icon
                                                        className="h-6 w-6"
                                                        strokeWidth={1.5}
                                                    />
                                                    {/* Icon label with transition */}
                                                    <span
                                                        className={cn(
                                                            "text-sm whitespace-nowrap overflow-hidden transition-all duration-300",
                                                            isActive
                                                                ? "text-md-on-primary-container"
                                                                : "text-md-on-surface-variant",
                                                            expanded || isMobile
                                                                ? "w-auto max-w-[200px] opacity-100 ml-1"
                                                                : "w-0 max-w-0 opacity-0 ml-0 p-0",
                                                        )}
                                                    >
                                                        {item.title}
                                                    </span>
                                                </div>
                                                <span
                                                    className={cn(
                                                        "text-xs text-center transition-all duration-300 truncate w-16",
                                                        isActive
                                                            ? "text-md-on-primary-container"
                                                            : "text-md-on-surface-variant",
                                                        expanded || isMobile
                                                            ? "max-h-0 opacity-0"
                                                            : "max-h-5 opacity-100 mt-1",
                                                    )}
                                                >
                                                    {item.title}
                                                </span>
                                            </Link>
                                        );
                                    })}
                                </div>

                                {/* Add divider if not the last group */}
                                {/* {groupIndex < groups.length - 1 && (
                                    <div className="mt-4 border-t border-md-outline-variant" />
                                )} */}
                            </div>
                        );
                    })}
                </div>

                {/* User Menu at bottom */}
                <div
                    className={cn(
                        "transition-all duration-300",
                        expanded || isMobile ? "px-4 py-3" : "px-3 py-3",
                    )}
                >
                    <UserMenu
                        collapsed={!expanded && !isMobile}
                        userName={userName}
                        userAvatarUrl={userAvatarUrl}
                        onSignOut={signOut}
                    />
                </div>
            </div>

            {/* Content padding for mobile */}
            <div className="md:hidden h-16" />

            {/* Mobile FAB */}
            {isMobile && <Fab />}
        </>
    );
}
